import { isCancel, CancelToken } from 'axios';
import { isEqual } from 'lodash';
import PropTypes from 'prop-types';
import { useRef, useState, useCallback, useEffect } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { Label } from '@hivebrite/design-system/Label';

import { axiosInstance as axios } from 'hb-react/shared/utils/axios';

import ReactSelect from '../ReactSelect';

const valueToIdParams = (value) => value?.id ?? value;

/**
* @deprecated but since alumni_connect ReactSelect
handle much more options format than @hivebrite/design-system/Inputs
we will need to use this one; waiting a unification of backend API.
*/
const ReactSelectAsync = ({
  apiMethod,
  creatable,
  dataParams,
  defaultValue,
  formatOptions,
  initialLoadOptions,
  loadOptionsParams,
  loadOptionsUrl,
  multiple,
  onChange,
  value,
  label,
  ...otherProps
}) => {
  const mounted = useRef(false);
  const cancelTokenRef = useRef(null);
  const [selectValue, setSelectValue] = useState(defaultValue);
  const [defaultOptions, setDefaultOptions] = useState(true);
  const [isLoading, setLoading] = useState(false);

  const cancelRequest = useCallback(() => cancelTokenRef.current?.cancel(), []);

  const handleLoadOptions = useCallback(
    (inputValue) => {
      // Cancel current request if we do another request
      cancelRequest();

      cancelTokenRef.current = CancelToken.source('Request canceled');

      const catchCancel = (err) => {
        if (isCancel(err)) {
          return [];
        }

        return err;
      };

      // Each time we enter new value in ReactSelect input, do an XHR call
      if (initialLoadOptions || (inputValue && inputValue !== '')) {
        setLoading(true);

        return axios({
          method: apiMethod,
          url: loadOptionsUrl,
          cancelToken: cancelTokenRef.current.token,
          ...(dataParams
            ? { data: { ...dataParams, term: inputValue } }
            : {
                params: {
                  size: 100,
                  ...loadOptionsParams,
                  term: inputValue,
                },
              }),
        })
          .then(({ data }) => {
            if (mounted.current) {
              setDefaultOptions(formatOptions(data));
            }

            setLoading(false);

            return formatOptions(data);
          })
          .catch(catchCancel);
      }

      value = value || defaultValue;

      // First call for display initial value in ReactSelect
      if (value) {
        // If multiple then [id, id, ...] else id
        const getParamsMultipleId = (items) =>
          items.includes(undefined) ? [] : items.map(valueToIdParams);
        const paramsId = multiple
          ? getParamsMultipleId(value)
          : valueToIdParams(value);

        // Only do XHR call if we have [id] or id
        const hasId = multiple ? paramsId.length > 0 : paramsId !== null;

        return hasId
          ? axios({
              method: apiMethod,
              url: loadOptionsUrl,
              cancelToken: cancelTokenRef.current.token,
              ...(dataParams
                ? { data: { ...dataParams, id: paramsId } }
                : {
                    params: {
                      ...loadOptionsParams,
                      id: paramsId,
                    },
                  }),
            })
              .then(({ data }) => {
                const options = Array.isArray(data) ? data : [data];

                let newValue = data;

                // Force newValue in Array if select is multiple
                if (multiple && !Array.isArray(data)) {
                  newValue = [data];
                }
                if (mounted.current) {
                  setDefaultOptions(formatOptions(options));
                  setSelectValue(newValue);
                }

                return formatOptions(options);
              })
              .catch(catchCancel)
          : Promise.resolve([]);
      }

      // Always return an array if we don't have result
      return Promise.resolve([]);
    },
    [
      loadOptionsParams,
      loadOptionsUrl,
      initialLoadOptions,
      defaultValue,
      value,
    ],
  );
  const handleChange = useCallback(
    (option) => {
      setSelectValue(option);
      if (onChange) {
        onChange(option);
      }
    },
    [onChange],
  );

  useEffect(() => {
    // Reload selectValue if props.value change
    let arrayValues;
    let arraySelectValues;

    if (multiple) {
      arrayValues =
        !value || value.includes(undefined)
          ? []
          : value.map(valueToIdParams).sort();
      arraySelectValues = selectValue
        ? selectValue.map(valueToIdParams).sort()
        : [];
    } else {
      arrayValues = [value];
      arraySelectValues = selectValue ? [selectValue.id] : [];
    }

    // normalize values on both arrays
    // We apply this conversion because in some cases the arrayValues and the arraySelectValues are Number or String.
    if (!isEqual(arrayValues.map(String), arraySelectValues.map(String))) {
      handleLoadOptions();
    }
  }, [defaultValue, value, selectValue]);

  useEffect(() => {
    mounted.current = true;

    // Cancel current request on Unmount
    return () => {
      mounted.current = false;
      cancelRequest();
    };
  }, []);

  return (
    <Wrapper label={label} {...otherProps}>
      <ReactSelect
        {...otherProps}
        isLoading={isLoading}
        async
        cacheOptions
        creatable={creatable}
        defaultOptions={defaultOptions}
        defaultValue={defaultValue}
        loadOptions={handleLoadOptions}
        multiple={multiple}
        onChange={handleChange}
        value={selectValue}
      />
    </Wrapper>
  );
};

const Wrapper = ({ label, children, ...props }) =>
  label ? (
    <Box flexDirection="column">
      <Label disabled={props.disabled} required={props.required} id={props.id}>
        {label}
      </Label>
      {children}
    </Box>
  ) : (
    children
  );

ReactSelectAsync.propTypes = {
  initialLoadOptions: PropTypes.bool,
  creatable: PropTypes.bool,
  loadOptionsUrl: PropTypes.string.isRequired,
  loadOptionsParams: PropTypes.object,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
    PropTypes.object,
  ]),
};

ReactSelectAsync.defaultProps = {
  apiMethod: 'get',
  formatOptions: (options) => options,
  initialLoadOptions: false,
  loadOptionsParams: {},
};

export default ReactSelectAsync;
